import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AtSign, ExternalLink, Plus, Sparkles, Truck, Undo2, UserCheck, UserRound, Users, X } from 'lucide-react';
import { buildTicketActors, isEmailAddress, outsideSourceLabel } from '../utils/ticketActors';
import { getStatusLabel } from '../constants/ticketStatuses';
import { formatShortDateTime } from '../utils/dates';
import './TicketActors.css';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

/**
 * « Intervenants » tab of the message window: the customer, the people in
 * copy (editable on e-mail tickets), the team members who wrote — from
 * Unanimity or outside it —, the assigned employee, the AI agent and the
 * carriers contacted.
 */
const TicketActors = ({ ticketId, ticket, messages, employeeName, aiAgentName, carrierConversation }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [newAddress, setNewAddress] = useState('');
    const [ccError, setCcError] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!ticketId) return;
        setLoading(true);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/actors`, { headers: authHeaders() });
            setData(response?.data || null);
            setLoadFailed(false);
        } catch (error) {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [ticketId]);

    useEffect(() => {
        load();
    }, [load]);

    const actors = useMemo(() => buildTicketActors({
        ticket,
        messages,
        employeeName,
        aiAgentName,
        carriers: data?.carriers,
        carrierConversation,
    }), [ticket, messages, employeeName, aiAgentName, data, carrierConversation]);

    const mailCopy = data?.mail_copy || null;
    const editable = !!mailCopy?.editable;

    const updateCopy = async (body) => {
        setSaving(true);
        setCcError('');
        try {
            const response = await axios.put(`/api/tickets/${ticketId}/cc`, body, { headers: authHeaders() });
            if (response?.data?.mail_copy) {
                setData((previous) => ({ ...(previous || {}), mail_copy: response.data.mail_copy }));
            } else {
                await load();
            }
            return true;
        } catch (error) {
            const code = error?.response?.status;
            if (code === 409) {
                setCcError(t('messageWindow.actors.ccNotMail', 'The copy can only be edited on e-mail tickets'));
            } else if (code === 422) {
                setCcError(t('messageWindow.actors.ccRejected', 'This address cannot be put in copy: invalid, already the recipient, or too many people in copy'));
            } else {
                setCcError(t('messageWindow.actors.ccError', 'The copy could not be updated'));
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async (event) => {
        event.preventDefault();
        const address = newAddress.trim().toLowerCase();
        if (!isEmailAddress(address)) {
            setCcError(t('messageWindow.actors.invalidEmail', 'Enter a valid e-mail address'));
            return;
        }
        if ((mailCopy?.addresses || []).includes(address)) {
            setNewAddress('');
            return;
        }
        if (await updateCopy({ add: [address] })) setNewAddress('');
    };

    const carrierStatus = (status) => (status
        ? t(`carriersConversation.status.${status}`, getStatusLabel(t, status))
        : '');

    const updatedAt = formatShortDateTime(mailCopy?.updated_at, undefined, { empty: '' });
    const { customers, team, ai, carriers } = actors;
    const hasTeam = team.fromUnanimity.length > 0 || team.outside.length > 0 || !!team.assigned;

    return (
        <div className="ticket-actors">
            <section className="ticket-actors-group" aria-labelledby="ticket-actors-customer">
                <h4 id="ticket-actors-customer" className="ticket-actors-heading">
                    <UserRound strokeWidth={1.8} aria-hidden="true" /> {t('messageWindow.actors.customer', 'Customer')}
                </h4>
                {customers.length === 0 ? (
                    <p className="ticket-actors-empty">{t('messageWindow.actors.noCustomer', 'No customer address on this ticket')}</p>
                ) : (
                    <ul className="ticket-actors-list">
                        {customers.map((customer) => (
                            <li key={customer.email} className="ticket-actors-item">
                                <span className="ticket-actors-name">{customer.email}</span>
                                {!customer.primary && (
                                    <span className="ticket-actors-meta">{t('messageWindow.actors.alsoWrote', 'also wrote')}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {(editable || (mailCopy?.addresses || []).length > 0) && (
                <section className="ticket-actors-group" aria-labelledby="ticket-actors-copy">
                    <h4 id="ticket-actors-copy" className="ticket-actors-heading">
                        <AtSign strokeWidth={1.8} aria-hidden="true" /> {t('messageWindow.actors.inCopy', 'In copy')}
                    </h4>
                    <p className="ticket-actors-hint">
                        {t('messageWindow.actors.ccHint', 'Replies sent from Unanimity, by the team or the AI, copy these people.')}
                    </p>
                    {(mailCopy?.addresses || []).length === 0 ? (
                        <p className="ticket-actors-empty">{t('messageWindow.actors.noCopy', 'Nobody in copy')}</p>
                    ) : (
                        <ul className="ticket-actors-chips">
                            {mailCopy.addresses.map((address) => (
                                <li key={address} className="u-tag ticket-actors-chip">
                                    <span>{address}</span>
                                    {editable && (
                                        <button
                                            type="button"
                                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm ticket-actors-chip-remove"
                                            onClick={() => updateCopy({ remove: [address] })}
                                            disabled={saving}
                                            aria-label={t('messageWindow.actors.removeCc', 'Remove {{email}} from copy', { email: address })}
                                            title={t('messageWindow.actors.removeCc', 'Remove {{email}} from copy', { email: address })}
                                        >
                                            <X strokeWidth={2} aria-hidden="true" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {editable && (
                        <form className="ticket-actors-add" onSubmit={handleAdd} noValidate>
                            <input
                                type="email"
                                className="u-input ticket-actors-input"
                                value={newAddress}
                                onChange={(event) => { setNewAddress(event.target.value); setCcError(''); }}
                                placeholder={t('messageWindow.actors.addCcPlaceholder', 'Add an address in copy')}
                                aria-label={t('messageWindow.actors.addCcPlaceholder', 'Add an address in copy')}
                                disabled={saving}
                            />
                            <button type="submit" className="u-btn u-btn--secondary ticket-actors-add-button" disabled={saving || !newAddress.trim()}>
                                <Plus strokeWidth={2} aria-hidden="true" /> {t('messageWindow.actors.addCc', 'Add')}
                            </button>
                        </form>
                    )}
                    {ccError && <p className="u-error-text ticket-actors-error" role="alert">{ccError}</p>}
                    {editable && (mailCopy?.removed || []).length > 0 && (
                        <div className="ticket-actors-removed">
                            <span className="ticket-actors-meta">{t('messageWindow.actors.removedTitle', 'Removed from copy')}</span>
                            <ul className="ticket-actors-chips">
                                {mailCopy.removed.map((address) => (
                                    <li key={address} className="u-tag ticket-actors-chip removed">
                                        <span>{address}</span>
                                        <button
                                            type="button"
                                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm ticket-actors-chip-remove"
                                            onClick={() => updateCopy({ add: [address] })}
                                            disabled={saving}
                                            aria-label={t('messageWindow.actors.readd', 'Put {{email}} back in copy', { email: address })}
                                            title={t('messageWindow.actors.readd', 'Put {{email}} back in copy', { email: address })}
                                        >
                                            <Undo2 strokeWidth={1.8} aria-hidden="true" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {editable && mailCopy?.updated_by && (
                        <p className="ticket-actors-meta ticket-actors-updated">
                            {t('messageWindow.actors.ccUpdatedBy', 'Last change by {{name}}', { name: mailCopy.updated_by })}
                            {updatedAt ? ` · ${updatedAt}` : ''}
                        </p>
                    )}
                </section>
            )}

            <section className="ticket-actors-group" aria-labelledby="ticket-actors-team">
                <h4 id="ticket-actors-team" className="ticket-actors-heading">
                    <Users strokeWidth={1.8} aria-hidden="true" /> {t('messageWindow.actors.team', 'Team')}
                </h4>
                {!hasTeam ? (
                    <p className="ticket-actors-empty">{t('messageWindow.actors.noTeam', 'No team member has written yet')}</p>
                ) : (
                    <ul className="ticket-actors-list">
                        {team.assigned && (
                            <li className="ticket-actors-item">
                                <UserCheck className="ticket-actors-item-icon" strokeWidth={1.8} aria-hidden="true" />
                                <span className="ticket-actors-name">{team.assigned}</span>
                                <span className="ticket-actors-meta">{t('messageWindow.actors.assigned', 'assigned')}</span>
                            </li>
                        )}
                        {team.fromUnanimity.map((member) => (
                            <li key={`u-${member.name}`} className="ticket-actors-item">
                                <span className="ticket-actors-name">{member.name}</span>
                                <span className="ticket-actors-meta">
                                    {t('messageWindow.actors.fromUnanimity', 'from Unanimity')} · {t('messageWindow.actors.messageCount', '{{count}} message(s)', { count: member.count })}
                                </span>
                            </li>
                        ))}
                        {team.outside.map((member) => (
                            <li key={`o-${member.name}-${member.source}`} className="ticket-actors-item outside">
                                <ExternalLink className="ticket-actors-item-icon" strokeWidth={1.8} aria-hidden="true" />
                                <span className="ticket-actors-name">{member.name || t('messageWindow.actors.unknownMember', 'Team member')}</span>
                                <span className="ticket-actors-meta">
                                    {t('messageWindow.actors.outside', 'outside Unanimity · {{source}}', { source: outsideSourceLabel(t, member.source) })} · {t('messageWindow.actors.messageCount', '{{count}} message(s)', { count: member.count })}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="ticket-actors-group" aria-labelledby="ticket-actors-ai">
                <h4 id="ticket-actors-ai" className="ticket-actors-heading">
                    <Sparkles strokeWidth={1.8} aria-hidden="true" /> {t('messageWindow.actors.aiAgent', 'AI agent')}
                </h4>
                {ai ? (
                    <ul className="ticket-actors-list">
                        <li className="ticket-actors-item">
                            <span className="ticket-actors-name">{ai.name || t('messageWindow.actors.aiAgentDefault', 'AI agent')}</span>
                            {ai.count > 0 && (
                                <span className="ticket-actors-meta">{t('messageWindow.actors.messageCount', '{{count}} message(s)', { count: ai.count })}</span>
                            )}
                        </li>
                    </ul>
                ) : (
                    <p className="ticket-actors-empty">{t('messageWindow.actors.noAi', 'No AI agent on this ticket')}</p>
                )}
            </section>

            <section className="ticket-actors-group" aria-labelledby="ticket-actors-carriers">
                <h4 id="ticket-actors-carriers" className="ticket-actors-heading">
                    <Truck strokeWidth={1.8} aria-hidden="true" /> {t('messageWindow.actors.carriers', 'Carriers contacted')}
                </h4>
                {carriers.length === 0 ? (
                    <p className="ticket-actors-empty">
                        {loading
                            ? t('messageWindow.actors.loading', 'Loading…')
                            : t('messageWindow.actors.noCarrier', 'No carrier contacted')}
                    </p>
                ) : (
                    <ul className="ticket-actors-list">
                        {carriers.map((carrier) => (
                            <li key={carrier.name} className="ticket-actors-item">
                                <span className="ticket-actors-name">{carrier.name}</span>
                                <span className="ticket-actors-meta">
                                    {[carrierStatus(carrier.status),
                                      carrier.conversations > 1
                                          ? t('messageWindow.actors.conversationCount', '{{count}} conversations', { count: carrier.conversations })
                                          : ''].filter(Boolean).join(' · ')}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {loadFailed && (
                <p className="u-error-text ticket-actors-error" role="alert">
                    {t('messageWindow.actors.loadError', 'The copy and the carriers could not be loaded')}
                </p>
            )}
        </div>
    );
};

export default TicketActors;
